import { GameMode, Difficulty } from '../constants.js';

/**
 * GameModeMenu - Minecraft-style game mode selection screen
 *
 * Shows when starting a new game (no save data) or when
 * "Cambiar Modo" is clicked from the Pause Menu.
 *
 * API:
 *   const menu = new GameModeMenu();
 *   const mode = await menu.show();  // 'survival' or 'creative'
 *   menu.hide();
 *   menu.isOpen  // boolean
 */

export class GameModeMenu {
  constructor() {
    this._open = false;
    this._selected = null; // 'survival' | 'creative'
    this._difficulty = Difficulty.NORMAL;
    this._resolve = null;

    this._buildDOM();
    document.body.appendChild(this._overlay);
  }

  // ==============================================================
  // Public API
  // ==============================================================

  /**
   * Show the menu. Returns a Promise that resolves with 'survival' or 'creative'
   * when the player confirms their choice.
   */
  show() {
    return new Promise((resolve) => {
      this._resolve = resolve;
      this._selected = null;
      this._difficulty = Difficulty.NORMAL;
      this._updateSelection();
      this._updateDifficulty();
      this._playBtn.style.opacity = '0.4';
      this._playBtn.style.pointerEvents = 'none';
      this._open = true;
      this._overlay.style.display = 'flex';
    });
  }

  /** Hide the menu */
  hide() {
    this._open = false;
    this._overlay.style.display = 'none';
    this._resolve = null;
  }

  /** Is the menu currently showing? */
  get isOpen() {
    return this._open;
  }

  // ==============================================================
  // DOM construction
  // ==============================================================

  _buildDOM() {
    // Overlay
    this._overlay = document.createElement('div');
    this._overlay.id = 'gamemode-menu-overlay';
    this._overlay.style.cssText = `
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0, 0, 0, 0.72);
      z-index: 950;
      display: none;
      justify-content: center;
      align-items: center;
      cursor: default;
    `;

    // Panel
    const panel = document.createElement('div');
    panel.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      background: #2A2A2A;
      border: 4px solid;
      border-top-color: #4A4A4A;
      border-left-color: #4A4A4A;
      border-bottom-color: #1A1A1A;
      border-right-color: #1A1A1A;
      padding: 28px 36px 24px 36px;
      min-width: 520px;
      max-width: 600px;
      image-rendering: pixelated;
    `;

    // Title
    const title = document.createElement('div');
    title.textContent = 'Seleccionar Modo de Juego';
    title.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 22px;
      font-weight: bold;
      color: #FFFFFF;
      text-shadow: 2px 2px 0 #3F3F3F;
      margin-bottom: 24px;
      letter-spacing: 1px;
      text-align: center;
    `;
    panel.appendChild(title);

    // Mode buttons container (side by side)
    const modesRow = document.createElement('div');
    modesRow.style.cssText = `
      display: flex;
      gap: 16px;
      margin-bottom: 20px;
      justify-content: center;
      width: 100%;
    `;

    // Survival card
    this._survivalCard = this._buildModeCard(
      GameMode.SURVIVAL,
      '/\\',
      'Supervivencia',
      'Vida, hambre, mobs hostiles, recursos limitados',
      '#737373', '#606060', '#8A8A8A'
    );

    // Creative card
    this._creativeCard = this._buildModeCard(
      GameMode.CREATIVE,
      ' * ',
      'Creativo',
      'Vuelo libre, bloques infinitos, sin dano',
      '#4A7A2E', '#3A6A1E', '#5A8A3E'
    );

    modesRow.appendChild(this._survivalCard);
    modesRow.appendChild(this._creativeCard);
    panel.appendChild(modesRow);

    // Difficulty label
    const diffLabel = document.createElement('div');
    diffLabel.textContent = 'Dificultad';
    diffLabel.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 14px;
      font-weight: bold;
      color: #CCCCCC;
      text-shadow: 1px 1px 0 #3F3F3F;
      margin-bottom: 8px;
    `;
    panel.appendChild(diffLabel);

    // Difficulty buttons row
    const diffRow = document.createElement('div');
    diffRow.style.cssText = 'display:flex;gap:8px;margin-bottom:18px;justify-content:center;';

    this._diffButtons = {};
    const diffs = [
      { id: Difficulty.PEACEFUL, label: 'Pacífico',  color: '#5A8A3E' },
      { id: Difficulty.EASY,     label: 'Fácil',     color: '#6A9A4E' },
      { id: Difficulty.NORMAL,   label: 'Normal',    color: '#737373' },
      { id: Difficulty.HARD,     label: 'Difícil',   color: '#8A3030' },
    ];
    for (const d of diffs) {
      const btn = document.createElement('div');
      btn.textContent = d.label;
      btn.dataset.diff = d.id;
      btn.style.cssText = `
        padding: 6px 12px;
        font-family: 'Courier New', monospace;
        font-size: 12px;
        font-weight: bold;
        color: #DDD;
        text-shadow: 1px 1px 0 #222;
        background: ${d.color};
        border: 2px solid;
        border-top-color: ${d.color}99;
        border-left-color: ${d.color}99;
        border-bottom-color: #222;
        border-right-color: #222;
        cursor: pointer;
        user-select: none;
      `;
      btn._baseColor = d.color;
      btn.addEventListener('click', () => {
        this._difficulty = d.id;
        this._updateDifficulty();
      });
      btn.addEventListener('mouseenter', () => {
        if (this._difficulty !== d.id) btn.style.filter = 'brightness(1.2)';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.filter = '';
      });
      diffRow.appendChild(btn);
      this._diffButtons[d.id] = btn;
    }
    panel.appendChild(diffRow);

    // Separator line
    const sep = document.createElement('div');
    sep.style.cssText = `
      width: 100%;
      height: 2px;
      background: linear-gradient(to right, transparent, #555555, transparent);
      margin-bottom: 18px;
    `;
    panel.appendChild(sep);

    // Play button
    this._playBtn = this._buildPlayButton();
    panel.appendChild(this._playBtn);

    this._overlay.appendChild(panel);
  }

  _buildModeCard(mode, iconText, titleText, descText, bgColor, bgDark, bgLight) {
    const card = document.createElement('div');
    card.dataset.mode = mode;
    card.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 220px;
      padding: 18px 14px 14px 14px;
      background: ${bgColor};
      border: 3px solid;
      border-top-color: ${bgLight};
      border-left-color: ${bgLight};
      border-bottom-color: ${bgDark};
      border-right-color: ${bgDark};
      cursor: pointer;
      user-select: none;
      box-sizing: border-box;
      transition: none;
    `;

    // Icon
    const icon = document.createElement('div');
    icon.textContent = iconText;
    icon.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 32px;
      font-weight: bold;
      color: #FFFFFF;
      text-shadow: 2px 2px 0 rgba(0,0,0,0.4);
      margin-bottom: 10px;
      line-height: 1;
    `;
    card.appendChild(icon);

    // Title
    const tEl = document.createElement('div');
    tEl.textContent = titleText;
    tEl.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 16px;
      font-weight: bold;
      color: #FFFFFF;
      text-shadow: 2px 2px 0 rgba(0,0,0,0.4);
      margin-bottom: 8px;
      text-align: center;
    `;
    card.appendChild(tEl);

    // Description
    const dEl = document.createElement('div');
    dEl.textContent = descText;
    dEl.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 11px;
      color: #D0D0D0;
      text-shadow: 1px 1px 0 rgba(0,0,0,0.3);
      text-align: center;
      line-height: 1.4;
    `;
    card.appendChild(dEl);

    // Hover
    card.addEventListener('mouseenter', () => {
      if (this._selected !== mode) {
        card.style.background = bgLight;
      }
    });
    card.addEventListener('mouseleave', () => {
      if (this._selected !== mode) {
        card.style.background = bgColor;
      }
    });

    // Click to select
    card.addEventListener('click', () => {
      this._selected = mode;
      this._updateSelection();
    });

    // Store references for selection update
    card._bgColor = bgColor;
    card._bgDark = bgDark;
    card._bgLight = bgLight;

    return card;
  }

  _buildPlayButton() {
    const btn = document.createElement('div');
    btn.textContent = 'Jugar';
    btn.style.cssText = `
      width: 200px;
      padding: 10px 0;
      text-align: center;
      font-family: 'Courier New', monospace;
      font-size: 18px;
      font-weight: bold;
      color: #E0E0E0;
      text-shadow: 2px 2px 0 #3F3F3F;
      background: #737373;
      border: 3px solid;
      border-top-color: #A0A0A0;
      border-left-color: #A0A0A0;
      border-bottom-color: #505050;
      border-right-color: #505050;
      cursor: pointer;
      user-select: none;
      image-rendering: pixelated;
      opacity: 0.4;
      pointer-events: none;
    `;

    btn.addEventListener('mouseenter', () => {
      if (this._selected) {
        btn.style.background = '#878787';
        btn.style.color = '#FFFFA0';
      }
    });

    btn.addEventListener('mouseleave', () => {
      btn.style.background = '#737373';
      btn.style.color = '#E0E0E0';
    });

    btn.addEventListener('mousedown', () => {
      if (this._selected) {
        btn.style.borderTopColor = '#505050';
        btn.style.borderLeftColor = '#505050';
        btn.style.borderBottomColor = '#A0A0A0';
        btn.style.borderRightColor = '#A0A0A0';
      }
    });

    btn.addEventListener('mouseup', () => {
      btn.style.borderTopColor = '#A0A0A0';
      btn.style.borderLeftColor = '#A0A0A0';
      btn.style.borderBottomColor = '#505050';
      btn.style.borderRightColor = '#505050';
    });

    btn.addEventListener('click', () => {
      if (this._selected && this._resolve) {
        const result = { mode: this._selected, difficulty: this._difficulty };
        const resolve = this._resolve;
        this._resolve = null;
        this.hide();
        resolve(result);
      }
    });

    return btn;
  }

  // ==============================================================
  // Selection state
  // ==============================================================

  _updateSelection() {
    const cards = [this._survivalCard, this._creativeCard];

    for (const card of cards) {
      const isSelected = card.dataset.mode === this._selected;

      if (isSelected) {
        // Golden selection border
        card.style.borderTopColor = '#FFD700';
        card.style.borderLeftColor = '#FFD700';
        card.style.borderBottomColor = '#B8960F';
        card.style.borderRightColor = '#B8960F';
        card.style.borderWidth = '3px';
        card.style.background = card._bgLight;
      } else {
        // Default border
        card.style.borderTopColor = card._bgLight;
        card.style.borderLeftColor = card._bgLight;
        card.style.borderBottomColor = card._bgDark;
        card.style.borderRightColor = card._bgDark;
        card.style.borderWidth = '3px';
        card.style.background = card._bgColor;
      }
    }

    // If creative selected, auto-set peaceful and hide difficulty row
    // (creative = no damage anyway)

    // Enable/disable Play button
    if (this._selected) {
      this._playBtn.style.opacity = '1';
      this._playBtn.style.pointerEvents = 'auto';
    } else {
      this._playBtn.style.opacity = '0.4';
      this._playBtn.style.pointerEvents = 'none';
    }
  }

  _updateDifficulty() {
    for (const [id, btn] of Object.entries(this._diffButtons)) {
      if (id === this._difficulty) {
        btn.style.borderTopColor = '#FFD700';
        btn.style.borderLeftColor = '#FFD700';
        btn.style.borderBottomColor = '#B8960F';
        btn.style.borderRightColor = '#B8960F';
        btn.style.filter = 'brightness(1.3)';
      } else {
        btn.style.borderTopColor = btn._baseColor + '99';
        btn.style.borderLeftColor = btn._baseColor + '99';
        btn.style.borderBottomColor = '#222';
        btn.style.borderRightColor = '#222';
        btn.style.filter = '';
      }
    }
  }
}
